import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="share-tickets"
//
// Drives the "Share Tickets" modal: each ticket has a checkbox; the action
// buttons (copy links / download / share) operate on the checked tickets.
//   - Copy links: copies the selected /t/:slug URLs, one per line.
//   - Download:   navigates to the share_ticket PDF endpoint with the selected
//                 slugs (server renders one page per ticket).
//   - Share:      native share sheet (mobile) with copy-link fallback (desktop).
export default class extends Controller {
  static targets = ["checkbox", "selectAll", "count", "shareButton"]
  static values = {
    downloadUrl: String,   // share_ticket PDF base path (e.g. /orders/share_ticket?slug=...&format=pdf)
    copiedMsgOne: String,
    copiedMsgOther: String, // contains %{count}
    noneMsg: String,
    shareTitle: String,
  }

  connect() {
    this.refresh()
    // The native share sheet only exists where the Web Share API is available
    // (mobile / touch). On desktop it falls back to copying, which duplicates
    // the Copy links button — so only reveal Share where it does something new.
    if (this.hasShareButtonTarget && navigator.share) {
      this.shareButtonTarget.classList.remove("d-none")
    }
  }

  // --- selection state ------------------------------------------------------
  toggleAll() {
    const checked = this.selectAllTarget.checked
    this.checkboxTargets.forEach((cb) => { cb.checked = checked })
    this.refresh()
  }

  refresh() {
    const total = this.checkboxTargets.length
    const selected = this.selectedCheckboxes().length
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = selected === total && total > 0
      this.selectAllTarget.indeterminate = selected > 0 && selected < total
    }
    if (this.hasCountTarget) this.countTarget.textContent = String(selected)
  }

  selectedCheckboxes() {
    return this.checkboxTargets.filter((cb) => cb.checked)
  }

  selectedSlugs() {
    return this.selectedCheckboxes().map((cb) => cb.dataset.slug)
  }

  selectedUrls() {
    return this.selectedCheckboxes().map((cb) => cb.dataset.url)
  }

  // --- actions --------------------------------------------------------------
  copyLinks(event) {
    event.preventDefault()
    const urls = this.selectedUrls()
    if (!this.ensureSelection(urls)) return

    if (this.copyToClipboard(urls.join("\n"))) {
      const msg = urls.length === 1
        ? this.copiedMsgOneValue
        : this.copiedMsgOtherValue.replace("%{count}", urls.length)
      this.flash("success", msg)
    }
  }

  download(event) {
    event.preventDefault()
    const slugs = this.selectedSlugs()
    if (!this.ensureSelection(slugs)) return

    const url = new URL(this.downloadUrlValue, window.location.origin)
    slugs.forEach((slug) => url.searchParams.append("purchased_ticket_slugs[]", slug))
    window.open(url.toString(), "_blank")
  }

  async share(event) {
    event.preventDefault()
    const urls = this.selectedUrls()
    if (!this.ensureSelection(urls)) return

    const text = urls.join("\n")
    if (navigator.share) {
      try {
        await navigator.share({ title: this.shareTitleValue, text })
      } catch (err) {
        if (err && err.name === "AbortError") return
        console.error("Share failed:", err)
        this.copyLinksFallback(urls)
      }
      return
    }
    this.copyLinksFallback(urls)
  }

  copyLinksFallback(urls) {
    if (this.copyToClipboard(urls.join("\n"))) {
      const msg = urls.length === 1
        ? this.copiedMsgOneValue
        : this.copiedMsgOtherValue.replace("%{count}", urls.length)
      this.flash("success", msg)
    }
  }

  // --- helpers --------------------------------------------------------------
  ensureSelection(items) {
    if (items.length === 0) {
      this.flash("error", this.noneMsgValue)
      return false
    }
    return true
  }

  flash(type, message) {
    if (typeof window.displayFlashMessage === "function") {
      window.displayFlashMessage(type, message)
    }
  }

  copyToClipboard(text) {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text)
        return true
      }
      // Non-secure context (e.g. http://lvh.me): fall back to execCommand. The
      // textarea must live INSIDE the modal — Bootstrap traps focus within the
      // modal, so a textarea on document.body can't be focused/selected while
      // the modal is open and the copy silently fails (leaving the clipboard
      // untouched). Appending to this.element keeps it in the focus-trapped area.
      const textArea = document.createElement("textarea")
      textArea.value = text
      textArea.setAttribute("readonly", "")
      textArea.style.position = "fixed"
      textArea.style.opacity = "0"
      textArea.style.left = "0"
      textArea.style.top = "0"
      this.element.appendChild(textArea)
      textArea.focus()
      textArea.select()
      textArea.setSelectionRange(0, text.length)
      try {
        const ok = document.execCommand("copy")
        textArea.remove()
        return ok
      } catch (err) {
        console.error("Failed to copy text:", err)
        textArea.remove()
        return false
      }
    } catch (err) {
      console.error("Failed to copy text:", err)
      return false
    }
  }
}
